import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import NotificationBell from './NotificationBell';
import { AppLauncher } from '@/core/oblique-ui';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';

// ─── All navigation items built-in (no SidebarNext dependency) ───
const NAV_SECTIONS = {
    crm: {
        title: 'CRM',
        items: [
            { label: 'Dashboard', path: '/crm', icon: 'dashboard' },
            { label: 'Reports', path: '/crm/reports', icon: 'bar_chart' },
            { label: 'Leads', path: '/crm/leads', icon: 'group_add' },
            { label: 'Deals', path: '/crm/pipeline', icon: 'handshake' },
            { label: 'Campaigns', path: '/crm/campaigns', icon: 'ads_click' },
            { label: 'Communication', path: '/crm/communication', icon: 'forum' },
            { label: 'Orders', path: '/crm/orders', icon: 'local_mall' },
            { label: 'Tickets', path: '/crm/tickets', icon: 'support_agent' },
            { label: 'Inbox', path: '/crm/inbox', icon: 'forum' },
            { label: 'Calendar', path: '/crm/planner', icon: 'calendar_today' },
        ]
    },
    operations: {
        title: 'Operations',
        items: [
            { label: 'Leads', path: '/operations/leads', icon: 'person_search' },
            { label: 'Companies', path: '/operations/companies', icon: 'apartment' },
            { label: 'Deals', path: '/operations/deals', icon: 'handshake' },
            { label: 'Activities', path: '/operations/activities', icon: 'event' },
            { label: 'Emails', path: '/operations/emails', icon: 'mail' },
            { label: 'Sequences', path: '/operations/sequences', icon: 'playlist_play' },
        ]
    },
    quality: {
        title: 'QMS & Audit',
        items: [
            { label: 'Dashboard', path: '/quality', icon: 'dashboard' },
            { label: 'Sales Audits', path: '/quality/sales-audits', icon: 'verified' },
            { label: 'BAU Audits', path: '/quality/audits', icon: 'fact_check' },

            { label: 'Escalations', path: '/quality/escalations', icon: 'warning' },
            { label: 'HubSpot Tracker', path: '/quality/leads', icon: 'track_changes' },
            { label: 'Compliance', path: '/quality/compliance', icon: 'policy' },
            { label: 'Agent Dashboard', path: '/quality/agent-dashboard', icon: 'person' },
            { label: 'Library', path: '/quality/library', icon: 'local_library' },
            { label: 'Planner', path: '/quality/planner', icon: 'calendar_today' },
            { label: 'Control Center', path: '/quality/control-center', icon: 'tune' },
            { label: 'Feedback', path: '/quality/feedback', icon: 'rate_review' },
        ]
    },
    analytics: {
        title: 'Intelligence',
        items: [
            { label: 'Dashboard', path: '/analytics', icon: 'dashboard' },
            { label: 'Forecasting', path: '/analytics/forecasting', icon: 'filter_alt' },
            { label: 'Calls', path: '/analytics/calls', icon: 'call' },
            { label: 'Meetings', path: '/analytics/meetings', icon: 'group' },
            { label: 'Leads', path: '/analytics/leads', icon: 'my_location' },
            { label: 'Deals', path: '/analytics/deals', icon: 'handshake' },
        ]
    },
    lms: {
        title: 'LMS',
        items: [
            { label: 'Dashboard', path: '/lms', icon: 'dashboard' },
            { label: 'Courses', path: '/lms/courses', icon: 'menu_book' },
            { label: 'Planner', path: '/lms/planner', icon: 'calendar_today' },
            { label: 'Intelligence', path: '/lms/intelligence', icon: 'hub' },
        ]
    },
    settings: {
        title: 'Settings',
        items: [
            { label: 'Profile', path: '/settings/preferences/profile', icon: 'person' },
            { label: 'General', path: '/settings/preferences', icon: 'settings' },
            { label: 'Notifications', path: '/settings/preferences/notifications', icon: 'notifications' },
            { label: 'Account', path: '/settings/account', icon: 'admin_panel_settings' },
            { label: 'Billing', path: '/settings/billing', icon: 'credit_card' },
            { label: 'Users & Teams', path: '/settings/users', icon: 'group' },
            { label: 'Connected Apps', path: '/settings/apps', icon: 'hub' },
            { label: 'Workflows', path: '/settings/workflows', icon: 'account_tree' },
        ]
    }
};

// Module switcher items for the drawer
const MODULES = [
    { id: 'crm', label: 'CRM', path: '/crm', icon: 'support_agent', color: '#3b82f6' },
    { id: 'quality', label: 'QMS & Audit', path: '/quality', icon: 'verified', color: '#059669' },
    { id: 'analytics', label: 'Intelligence', path: '/analytics', icon: 'analytics', color: '#9333ea' },
    { id: 'lms', label: 'LMS', path: '/lms', icon: 'school', color: '#f59e0b' },
    { id: 'settings', label: 'Settings', path: '/settings/preferences/profile', icon: 'settings', color: '#64748b' },
];

function getActiveSection(pathname) {
    if (pathname.startsWith('/quality')) return 'quality';
    if (pathname.startsWith('/analytics')) return 'analytics';
    if (pathname.startsWith('/lms')) return 'lms';
    if (pathname.startsWith('/settings')) return 'settings';
    if (pathname.startsWith('/operations')) return 'operations';
    return 'crm';
}

export default function MobileLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData } = useAuth();
    const { currentCompany } = useCompany();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const activeSection = getActiveSection(location.pathname);
    const navSection = NAV_SECTIONS[activeSection];

    // Get current module for title
    let headerTitle = "Sales";
    if (location.pathname.startsWith('/quality')) headerTitle = "QMS";
    else if (location.pathname.startsWith('/analytics')) headerTitle = "Insights";
    else if (location.pathname.startsWith('/settings')) headerTitle = "Settings";
    else if (location.pathname.startsWith('/lms')) headerTitle = "LMS";
    else if (location.pathname.startsWith('/operations')) headerTitle = "Operations";

    const handleNav = (path) => {
        navigate(path);
        setIsDrawerOpen(false);
    };

    return (
        <div className="mobile-layout-wrapper">
            {/* 1. Mobile Top Header */}
            <header className="mobile-top-header">
                <button
                    className="mobile-header-btn"
                    onClick={() => setIsDrawerOpen(true)}
                    aria-label="Open navigation menu"
                >
                    <ObliqueIcon name="menu" size={24} />
                </button>

                <div className="mobile-header-title">
                    {headerTitle}
                </div>

                <div className="mobile-header-actions">
                    <AppLauncher />
                    <NotificationBell />
                    <button className="mobile-header-btn" onClick={() => navigate('/settings/preferences/profile')}>
                        <div className="mobile-avatar">
                            {userData?.photoURL
                                ? <img src={userData.photoURL} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <span>{(userData?.displayName || 'U')[0]}</span>
                            }
                        </div>
                    </button>
                </div>
            </header>

            {/* 2. Native Side Drawer — fully self-contained, no SidebarNext */}
            <AnimatePresence>
                {isDrawerOpen && (
                    <>
                        <motion.div
                            className="mobile-drawer-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsDrawerOpen(false)}
                        />
                        <motion.div
                            className="mobile-side-drawer"
                            initial={{ x: '-100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '-100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        >
                            {/* Drawer Header */}
                            <div className="mobile-drawer-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div className="mobile-drawer-logo">
                                        {currentCompany?.logoUrl
                                            ? <img src={currentCompany.logoUrl} alt="" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                                            : <ObliqueIcon name="apartment" size={20} />
                                        }
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text-primary)' }}>
                                            {currentCompany?.name || 'Workspace'}
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                                            {userData?.email}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Module Switcher Chips */}
                            <div className="mobile-drawer-modules">
                                {MODULES.map(mod => {
                                    const isActive = activeSection === mod.id;
                                    return (
                                        <button
                                            key={mod.id}
                                            className={`mobile-module-chip ${isActive ? 'active' : ''}`}
                                            style={{ '--chip-color': mod.color }}
                                            onClick={() => handleNav(mod.path)}
                                        >
                                            <ObliqueIcon name={mod.icon} size={16} />
                                            <span>{mod.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Navigation Links for Active Module */}
                            <div className="mobile-drawer-nav">
                                <div className="mobile-drawer-section-title">
                                    {navSection?.title || 'Navigation'}
                                </div>
                                {navSection?.items?.map(item => {
                                    const isActive = location.pathname === item.path ||
                                        (item.path !== '/crm' && item.path !== '/quality' && item.path !== '/analytics' && item.path !== '/lms' && location.pathname.startsWith(item.path));
                                    return (
                                        <button
                                            key={item.path}
                                            className={`mobile-drawer-item ${isActive ? 'active' : ''}`}
                                            onClick={() => handleNav(item.path)}
                                        >
                                            <ObliqueIcon name={item.icon} size={20} />
                                            <span>{item.label}</span>
                                            {isActive && <div className="mobile-drawer-active-dot" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* 3. Bottom Tab Bar */}
            <nav className="mobile-bottom-tabs">
                <button
                    className={`mobile-tab-btn ${location.pathname === '/crm' || location.pathname === '/' ? 'active' : ''}`}
                    onClick={() => navigate('/crm')}
                >
                    <ObliqueIcon name="home" size={22} />
                    <span>Home</span>
                </button>
                <button
                    className={`mobile-tab-btn ${location.pathname.startsWith('/crm/leads') || location.pathname.startsWith('/operations/leads') ? 'active' : ''}`}
                    onClick={() => navigate('/crm/leads')}
                >
                    <ObliqueIcon name="group_add" size={22} />
                    <span>Leads</span>
                </button>
                <button
                    className={`mobile-tab-btn ${location.pathname.startsWith('/crm/pipeline') || location.pathname.startsWith('/operations/deals') ? 'active' : ''}`}
                    onClick={() => navigate('/crm/pipeline')}
                >
                    <ObliqueIcon name="handshake" size={22} />
                    <span>Deals</span>
                </button>
                <button
                    className={`mobile-tab-btn ${location.pathname.startsWith('/quality') ? 'active' : ''}`}
                    onClick={() => navigate('/quality/sales-audits')}
                >
                    <ObliqueIcon name="verified" size={22} />
                    <span>QMS</span>
                </button>
                <button
                    className={`mobile-tab-btn`}
                    onClick={() => setIsDrawerOpen(true)}
                >
                    <ObliqueIcon name="more_horiz" size={22} />
                    <span>More</span>
                </button>
            </nav>
        </div>
    );
}
